import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ImprovedNoise } from 'three/examples/jsm/math/ImprovedNoise';

const NebulaField: React.FC<{ count?: number }> = ({ count = 1500 }) => {
    const pointsRef = useRef<THREE.Points>(null!);
    const materialRef = useRef<THREE.PointsMaterial>(null!);
    const noise = useMemo(() => new ImprovedNoise(), []);

    const baseSize = useMemo(() => 5 * Math.random(), []);

    const positions = useMemo(() => {
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            arr[i * 3 + 0] = (Math.random() - 0.5) * 30; // x
            arr[i * 3 + 1] = (Math.random() - 0.5) * 20; // y
            arr[i * 3 + 2] = (Math.random() - 0.5) * 10; // z
        }
        return arr;
    }, [count]);

    const colors = useMemo(() => new Float32Array(count * 3), [count]);

    useFrame(({ clock }) => {
        const t = clock.getElapsedTime();
        const geo = pointsRef.current.geometry as THREE.BufferGeometry;
        const pos = geo.getAttribute('position') as THREE.BufferAttribute;
        const col = geo.getAttribute('color') as THREE.BufferAttribute;

        for (let i = 0; i < count; i++) {
            const ix = i * 3;

            const x = pos.array[ix];
            const y = pos.array[ix + 1];
            const z = pos.array[ix + 2];

            // Bewegung auf Basis von Noise – sehr weich
            const dx = noise.noise(x * 0.05, t * 0.05, z * 0.05) * 0.01;
            const dy = noise.noise(y * 0.05, t * 0.05 + 10, x * 0.05) * 0.01;
            const dz = noise.noise(z * 0.05, t * 0.05 + 20, y * 0.05) * 0.01;

            pos.array[ix] += dx;
            pos.array[ix + 1] += dy;
            pos.array[ix + 2] += dz;

            // Leichte Farbvariation per Tiefe/Zeit
            const fade = 0.8 + 0.1 * Math.sin(t * 0.2 + z);
            col.array[ix + 0] = Math.random() * fade;
            col.array[ix + 1] = Math.random() * fade;
            col.array[ix + 2] = Math.random() * fade;
        }

        pos.needsUpdate = true;
        col.needsUpdate = true;

        // Materialgröße sanft modulieren
        materialRef.current.size = baseSize * (1 + 0.2 * Math.sin(t * 0.5));
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[positions, 3]} />
                <bufferAttribute attach="attributes-color" args={[colors, 3]} />
            </bufferGeometry>
            <pointsMaterial
                ref={materialRef}
                vertexColors
                size={baseSize}
                sizeAttenuation
                transparent
                opacity={1}
                blending={THREE.AdditiveBlending}
                depthWrite={false}
            />
        </points>
    );
};

export default NebulaField;